import React from 'react';

/**
 * Ingram Micro Xvantage placeholder lockup. Explicitly stylised so reviewers
 * know real brand goes here. Geometric "X" mark + wordmark.
 */
export function IngramLockup() {
  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="relative w-7 h-7 grid place-items-center rounded-md bg-slate-900 text-white">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      </div>
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium">
          Ingram Micro
        </div>
        <div className="text-[13px] font-semibold text-slate-900 -mt-0.5">
          Xvantage<span className="text-slate-400 text-[10px] align-top ml-0.5">↗</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Co-branded Aitryx × Ingram Micro Xvantage lockup. Used in the connector
 * header so Ingram executives see partnership framing on every screen.
 */
export function AitryxIngramCobrand() {
  return (
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-md grid place-items-center bg-gradient-to-br from-orange-500 to-orange-600 shadow-[0_2px_6px_rgba(249,115,22,0.3)]">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M7 1 L13 13 H1 Z" fill="white" />
          </svg>
        </div>
        <span className="text-[13px] font-semibold tracking-tight text-slate-900">aitryx</span>
      </div>
      <span className="text-slate-300 text-[16px] font-light">×</span>
      <IngramLockup />
    </div>
  );
}

export function DickerDataLockup() {
  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="relative w-7 h-7 grid place-items-center rounded-md bg-cyan-700 text-white">
        <span className="text-[10px] font-bold tracking-tight">DD</span>
      </div>
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium">
          Dicker Data
        </div>
        <div className="text-[13px] font-semibold text-slate-900 -mt-0.5">
          B2B API
        </div>
      </div>
    </div>
  );
}

export function AitryxDickerDataCobrand() {
  return (
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-md grid place-items-center bg-gradient-to-br from-orange-500 to-orange-600 shadow-[0_2px_6px_rgba(249,115,22,0.3)]">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M7 1 L13 13 H1 Z" fill="white" />
          </svg>
        </div>
        <span className="text-[13px] font-semibold tracking-tight text-slate-900">aitryx</span>
      </div>
      <span className="text-slate-300 text-[16px] font-light">×</span>
      <DickerDataLockup />
    </div>
  );
}

export function DistributorLockup({
  distributorId,
  distributorLabel,
}: {
  distributorId: string;
  distributorLabel: string;
}) {
  if (distributorId === 'ingram') return <IngramLockup />;
  if (distributorId === 'dickerdata' || distributorId === 'dicker-data') {
    return <DickerDataLockup />;
  }
  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="relative w-7 h-7 grid place-items-center rounded-md bg-slate-700 text-white">
        <span className="text-[10px] font-bold tracking-tight">
          {distributorLabel.slice(0, 2).toUpperCase()}
        </span>
      </div>
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium">
          {distributorLabel}
        </div>
        <div className="text-[13px] font-semibold text-slate-900 -mt-0.5">
          Distributor
        </div>
      </div>
    </div>
  );
}

/**
 * Vendor monogram chip. 2-letter abbreviation in a tinted square; used in
 * place of real vendor logos. Pass vendor name; tint resolves from VENDOR_TINTS.
 */
const VENDOR_TINTS: Record<string, { tint: string; mono: string }> = {
  hp: { tint: '#0096D6', mono: 'HP' },
  lenovo: { tint: '#E2231A', mono: 'LE' },
  dell: { tint: '#007DB8', mono: 'DE' },
  cisco: { tint: '#1BA0D7', mono: 'CI' },
  microsoft: { tint: '#737373', mono: 'MS' },
  apc: { tint: '#3DAE2B', mono: 'AP' },
  logitech: { tint: '#00B8FC', mono: 'LG' },
  jabra: { tint: '#FFCC33', mono: 'JA' },
  samsung: { tint: '#1428A0', mono: 'SA' },
  epson: { tint: '#003399', mono: 'EP' },
};

function vendorKey(name: string | null | undefined): string {
  if (!name) return '';
  const lower = name.toLowerCase();
  for (const k of Object.keys(VENDOR_TINTS)) {
    if (lower.includes(k)) return k;
  }
  return '';
}

export function VendorMark({ vendor, size = 24 }: { vendor: string | null | undefined; size?: number }) {
  const key = vendorKey(vendor);
  const meta = VENDOR_TINTS[key];
  const tint = meta?.tint ?? '#94a3b8';
  const mono = meta?.mono ?? (vendor ? vendor.slice(0, 2).toUpperCase() : '??');
  const fontSize = size <= 20 ? 9 : size <= 28 ? 10 : 12;
  return (
    <div
      title={vendor || undefined}
      style={{
        width: size,
        height: size,
        borderColor: tint + '40',
        color: tint,
        fontSize,
      }}
      className="inline-flex items-center justify-center rounded-md border bg-white font-semibold tracking-tight shrink-0"
    >
      {mono}
    </div>
  );
}
